import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

const DocumentPreview = ({ document, onClose, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDocument, setEditedDocument] = useState({
    name: document?.name,
    tags: document?.tags?.join(', ') || '',
    description: document?.description || ''
  });

  const handleSave = () => {
    const updatedDocument = {
      ...document,
      name: editedDocument?.name,
      tags: editedDocument?.tags?.split(',')?.map(tag => tag?.trim())?.filter(tag => tag),
      description: editedDocument?.description
    };
    onUpdate(updatedDocument);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedDocument({
      name: document?.name,
      tags: document?.tags?.join(', ') || '',
      description: document?.description || ''
    });
    setIsEditing(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf': return 'FileText';
      case 'jpg': case'jpeg': case'png': case'gif': return 'Image';
      case 'doc': case'docx': return 'FileText';
      case 'xls': case'xlsx': return 'FileSpreadsheet';
      default: return 'File';
    }
  };

  const canPreview = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt']?.includes(document?.type?.toLowerCase());

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex">
      {/* Preview Panel */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-surface border border-border rounded-lg shadow-floating max-w-4xl max-h-full overflow-hidden">
          {/* Preview Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <Icon name={getFileIcon(document?.type)} size={24} className="text-primary" />
              <div>
                <h2 className="text-lg font-semibold text-foreground">{document?.name}</h2>
                <p className="text-sm text-muted-foreground">
                  {document?.type?.toUpperCase()} • {formatFileSize(document?.size)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Download">
                Download
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="p-4" style={{ maxHeight: '60vh', overflow: 'auto' }}>
            {canPreview ? (
              <div className="flex items-center justify-center bg-muted/30 rounded-lg" style={{ minHeight: '400px' }}>
                {document?.type?.toLowerCase() === 'pdf' ? (
                  <div className="text-center">
                    <Icon name="FileText" size={64} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">PDF Preview</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Click download to view the full document
                    </p>
                  </div>
                ) : document?.thumbnail || ['jpg', 'jpeg', 'png', 'gif']?.includes(document?.type?.toLowerCase()) ? (
                  <Image
                    src={document?.thumbnail || '/assets/images/no_image.png'}
                    alt={document?.name}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="text-center">
                    <Icon name="FileText" size={64} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Text Document Preview</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Document content will be displayed here
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center bg-muted/30 rounded-lg" style={{ minHeight: '400px' }}>
                <div className="text-center">
                  <Icon name={getFileIcon(document?.type)} size={64} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Preview not available</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Download the file to view its contents
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Details Panel */}
      <div className="w-80 bg-surface border-l border-border overflow-y-auto">
        <div className="p-4">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-foreground">Document Details</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
              iconName="Edit"
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>
          </div>

          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Basic Information</h4>
              <div className="space-y-3">
                {isEditing ? (
                  <Input
                    label="File Name"
                    value={editedDocument?.name}
                    onChange={(e) => setEditedDocument(prev => ({ ...prev, name: e?.target?.value }))}
                  />
                ) : (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">File Name</label>
                    <p className="text-sm text-foreground mt-1">{document?.name}</p>
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-muted-foreground">File Type</label>
                  <p className="text-sm text-foreground mt-1">{document?.type?.toUpperCase()}</p>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">File Size</label>
                  <p className="text-sm text-foreground mt-1">{formatFileSize(document?.size)}</p>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">Upload Date</label>
                  <p className="text-sm text-foreground mt-1">{formatDate(document?.uploadDate)}</p>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Tags</h4>
              {isEditing ? (
                <Input
                  placeholder="Enter tags separated by commas"
                  value={editedDocument?.tags}
                  onChange={(e) => setEditedDocument(prev => ({ ...prev, tags: e?.target?.value }))}
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {document?.tags && document?.tags?.length > 0 ? (
                    document?.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No tags added</p>
                  )}
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Description</h4>
              {isEditing ? (
                <textarea
                  className="w-full p-3 border border-border rounded-lg bg-background text-foreground text-sm resize-none"
                  rows={4}
                  placeholder="Add a description..."
                  value={editedDocument?.description}
                  onChange={(e) => setEditedDocument(prev => ({ ...prev, description: e?.target?.value }))}
                />
              ) : (
                <p className="text-sm text-foreground">
                  {document?.description || 'No description added'}
                </p>
              )}
            </div>

            {/* AI Insights */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">AI Insights</h4>
              <div className="space-y-2">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Brain" size={16} className="text-primary" />
                    <span className="text-xs font-medium text-primary">Content Analysis</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    AI analysis will appear here after document processing
                  </p>
                </div>
                
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Link" size={16} className="text-accent" />
                    <span className="text-xs font-medium text-accent">Suggested Links</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Related documents and connections will be suggested here
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            {isEditing ? (
              <div className="flex space-x-2">
                <Button onClick={handleSave} size="sm" className="flex-1">
                  Save Changes
                </Button>
                <Button variant="outline" onClick={handleCancel} size="sm" className="flex-1">
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Button variant="outline" size="sm" className="w-full" iconName="Share">
                  Share Document
                </Button>
                <Button variant="outline" size="sm" className="w-full" iconName="Copy">
                  Copy Link
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="w-full" 
                  iconName="Trash2"
                  onClick={() => onDelete(document?.id)}
                >
                  Delete Document
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;